/**
 * Billing & Analytics Page
 * Super Admin Only
 */

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import useCurrentUser from '../../hooks/useCurrentUser';

function Billing() {
    const { getAccessTokenSilently } = useAuth0();
    const { isSuperAdmin, loading: userLoading } = useCurrentUser();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState([]);
    const [settings, setSettings] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState(null);

    // Initial Pricing State (will be overwritten by backend)
    const [pricing, setPricing] = useState({
        starter: { name: 'Starter', price: 199, currency: 'USD' },
        pro: { name: 'Pro', price: 499, currency: 'USD' },
        enterprise: { name: 'Enterprise', price: 1500, currency: 'USD' }
    });

    useEffect(() => {
        if (isSuperAdmin) {
            loadData();
        }
    }, [isSuperAdmin]);

    const loadData = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const headers = { Authorization: `Bearer ${token}` };

            const [statsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/billing/stats', { headers }),
                axios.get('/api/admin/settings', { headers })
            ]);

            setStats(statsRes.data.report);

            if (settingsRes.data.settings.pricing_tiers) {
                setPricing(settingsRes.data.settings.pricing_tiers);
                setSettings(settingsRes.data.settings);
            }
        } catch (err) {
            console.error('Error loading billing data:', err);
            setMessage({ type: 'danger', text: 'Failed to load data.' });
        } finally {
            setLoading(false);
        }
    };

    const handlePricingChange = (tier, field, value) => {
        setPricing(prev => ({
            ...prev,
            [tier]: {
                ...prev[tier],
                [field]: field === 'price' ? parseFloat(value) : value
            }
        }));
    };

    const savePricing = async () => {
        try {
            setIsSaving(true);
            const token = await getAccessTokenSilently();

            await axios.put('/api/admin/settings', {
                key: 'pricing_tiers',
                value: pricing,
                description: 'Global subscription pricing tiers'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage({ type: 'success', text: 'Pricing updated successfully!' });
            setTimeout(() => setMessage(null), 3000);
        } catch (err) {
            console.error('Error saving pricing:', err);
            setMessage({ type: 'danger', text: 'Failed to update pricing.' });
        } finally {
            setIsSaving(false);
        }
    };

    const calculateEstCost = (plan, smsCount, webCount) => {
        // Simple logic: Base plan cost. 
        // In a real app, this might include overage calculations.
        const tier = Object.values(pricing).find(t => t.name.toLowerCase() === plan?.toLowerCase());
        return tier ? `$${tier.price}` : '-';
    };

    if (userLoading) return <div className="p-5 text-center">Loading...</div>;
    if (!isSuperAdmin) return <div className="p-5 text-center">Access Denied</div>;
    if (loading) return <div className="p-5 text-center">Loading Billing Data...</div>;

    return (
        <div className="container-fluid py-4">
            <h2 className="mb-4">Billing & Analytics</h2>

            {message && (
                <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
                </div>
            )}

            {/* Pricing Configuration Card */}
            <div className="card shadow-sm mb-5">
                <div className="card-header bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Subscription Pricing</h5>
                        <button
                            className="btn btn-primary"
                            onClick={savePricing}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    <div className="row">
                        {Object.keys(pricing).map(tierKey => (
                            <div className="col-md-4" key={tierKey}>
                                <div className="p-3 border rounded bg-light h-100">
                                    <h6 className="text-uppercase text-muted mb-3">{tierKey} Plan</h6>

                                    <div className="mb-3">
                                        <label className="form-label small">Display Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={pricing[tierKey].name}
                                            onChange={(e) => handlePricingChange(tierKey, 'name', e.target.value)}
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-8">
                                            <label className="form-label small">Price</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={pricing[tierKey].price}
                                                onChange={(e) => handlePricingChange(tierKey, 'price', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-4">
                                            <label className="form-label small">Currency</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={pricing[tierKey].currency}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Usage Report Card */}
            <div className="card shadow-sm">
                <div className="card-header bg-white py-3">
                    <h5 className="mb-0">Tenant Usage (Last 30 Days)</h5>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4">Organization</th>
                                <th>Plan Status</th>
                                <th>SMS Convos</th>
                                <th>Web Chats</th>
                                <th>Billing Est.</th>
                                <th>Last Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.map(org => (
                                <tr key={org.orgId}>
                                    <td className="ps-4">
                                        <div className="fw-bold">{org.name}</div>
                                        <div className="text-muted small font-monospace">{org.orgId}</div>
                                    </td>
                                    <td>
                                        <span className={`badge ${org.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                                            {org.plan} ({org.status})
                                        </span>
                                    </td>
                                    <td>{org.smsConversations}</td>
                                    <td>{org.webConversations}</td>
                                    <td className="fw-bold text-success">
                                        {calculateEstCost(org.plan, org.smsConversations, org.webConversations)}
                                    </td>
                                    <td className="text-muted small">
                                        {org.lastActive ? new Date(org.lastActive).toLocaleDateString() : 'Never'}
                                    </td>
                                </tr>
                            ))}
                            {stats.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">No usage data found (or no tenants).</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default Billing;
